import React from 'react'
import { Control, FieldErrors, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form'
import { msgid, ngettext, t } from 'ttag'
import { announce } from '../../../../components/a11y/useAnnouncer'
import { CHALLENGE_ADMIN_STEP_GOAL, FormData } from '../../ChallengeAdmin'
import { CustomTypeCategory, customTypes } from '../../../../services/CustomTypes'
import { isSimpleDayType } from '../../../../common/helpers/challenges'
import YumAccordion from '../../../../components/YumAccordion/YumAccordion'
import YumAccordionSummary from '../../../../components/YumAccordionSummary/YumAccordionSummary'
import Typography from '@material-ui/core/Typography'
import { isRtl } from '../../../../i18nInit'
import { AccordionDetails, IconButton } from '@material-ui/core'
import { ReactComponent as ConsistencyTargetInactiveIcon } from '../../../../assets/v3/icons/consistency-target-inactive.svg'
import YumRemoveButton from '../../../../components/YumRemoveButton/YumRemoveButton'
import { ReactComponent as RemoveIcon } from '../../../../assets/v3/icons/remove.svg'

// Import shared helpers
import { getFieldName } from './goalFormHelpers'
import { useDailyGoalUnit, useConsistencyUnit } from './goalUnitHelpers'
import { getConsistencyGoalValidationRules, shouldShowCustomUnitInput } from './goalValidationHelpers'

// Import field components
import ConsistencyGoalField from './fields/ConsistencyGoalField'
import IndexTargetField from './fields/IndexTargetField'

interface GoalAccordionProps {
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  formStep: number
  // Field name prefix for nested goals (e.g., 'teamGoal', 'groupGoals.categoryId', 'countryGoal')
  fieldPrefix: string
  // Whether this is an individual goal (affects consistency unit text and max validation).
  // For team, country, etc. goals this should be false.
  isIndividualGoal?: boolean
  hideRemove?: boolean
}

/**
 * Total goal accordion component.
 * Renders a consistency goal accordion with appropriate input fields.
 * Allows users to set daily goals and consistency requirements.
 */
export default function ConsistencyGoal(props: GoalAccordionProps) {
  const {
    control,
    watch,
    setValue,
    errors,
    activityType,
    formStep,
    fieldPrefix,
    isIndividualGoal = true,
    hideRemove = false,
  } = props

  // Watch form values needed for the component
  const goalType = watch('goalType')
  //const customUnit = watch('customUnit')
  const customUnit = useWatch({ control, name: 'customUnit' })
  const customType = useWatch({ control, name: 'customType' })
  const simpleManualEntry = useWatch({ control, name: 'simpleManualEntry' })
  const days = watch('days')

  // Check if this is a defined custom activity type
  const isDefinedCustomActivity = customTypes.some((ct) => ct.id === activityType)

  // Get validation rules for all fields
  const validationRules = getConsistencyGoalValidationRules(
    formStep,
    activityType,
    goalType,
    isDefinedCustomActivity,
    simpleManualEntry,
  )

  // Calculate unit texts using custom hooks
  // Handle null/undefined customType safely
  const dailyGoalUnit = useDailyGoalUnit(activityType, customType ?? null, customUnit ?? '')
  const consistencyUnitText = isIndividualGoal ? useConsistencyUnit(days ?? 0) : ngettext(msgid`day`, `days`, days ?? 2)

  // Determine which fields to show
  //const showCustomUnitInput = shouldShowCustomUnitInput(activityType, simpleManualEntry)

  // Get the current goal for this specific goal
  // When fieldPrefix is empty, use 'goals.individual' as the path for individual goals
  const goalPath = fieldPrefix || 'goals.individual'
  const currentGoal = watch(goalPath as any)

  // Handler to remove/disable the goal
  const handleRemoveGoal = () => {
    if (currentGoal && currentGoal.enabled) {
      setValue(goalPath as any, { ...currentGoal, enabled: false })
      // Assertive so it isn't dropped while the goal fields are removed from the DOM.
      announce(t`Goal removed.`, 'assertive')
    }
  }

  // Build consistency goal inputs based on activity type and conditions
  const consistencyGoalInputs = (
    <>
      {/* Daily target is handled in the parent step to keep a single shared field. */}
      {/* Custom unit input for custom activity types (when simple manual entry is disabled) */}
      {/* {showCustomUnitInput && (
        <CustomUnitInput control={control} fieldPrefix={fieldPrefix} validationRules={validationRules.customUnit} />
      )} */}
      {/* Custom unit field for defined activities (not POINTS) */}
      {/* {isDefinedCustomActivity && activityType !== CustomTypeCategory.POINTS && (
        <CustomUnitField
          control={control}
          fieldPrefix={fieldPrefix}
          activityType={activityType}
          formStep={formStep}
          validationRules={validationRules.customType}
        />
      )} */}
      {/* Index target field for ALL activity type */}
      {/* {activityType === CustomTypeCategory.ALL && (
        <IndexTargetField
          control={control}
          fieldPrefix={fieldPrefix}
          validationRules={validationRules.indexTarget}
          unitText={dailyGoalUnit}
          isDailyTarget
        />
      )} */}
      {/* Total goal field (always shown) */}
      <ConsistencyGoalField
        control={control}
        fieldPrefix={fieldPrefix}
        validationRules={{
          ...validationRules.persistenceGoal,
          // Set max dynamically based on days (only for individual goals)
          max:
            isIndividualGoal && formStep === CHALLENGE_ADMIN_STEP_GOAL && goalType === 'consistentDays'
              ? days
              : undefined,
        }}
        // Only show consistency unit text for individual goals (not for teams, countries, etc.)
        consistencyUnitText={consistencyUnitText}
        // Hide top margin for simple entry types (CUSTOM with simpleManualEntry, or simple day types)
        hideTopMargin={
          (!isIndividualGoal ||
            (activityType === CustomTypeCategory.CUSTOM && simpleManualEntry) ||
            isSimpleDayType(activityType)) &&
          activityType !== CustomTypeCategory.ALL
        }
      />
    </>
  )

  return (
    <>
      {/* // <YumAccordion expanded={true}>
    //   <YumAccordionSummary
    //     expandIcon={null}
    //     style={{
    //       justifyContent: 'space-between',
    //     }}
    //   >
    //     <ConsistencyTargetInactiveIcon />
    //     <Typography
    //       variant="body2"
    //       style={isRtl ? { marginRight: '10px', flexGrow: 1 } : { marginLeft: '10px', flexGrow: 1 }}
    //     >
    //       {t`Total goal`}
    //     </Typography> */}
      {/* <YumRemoveButton
        onClick={handleRemoveGoal}
        style={{
          flexShrink: 0,
          marginRight: '-7px',
          padding: '8px',
          float: 'right',
        }}
      /> */}
      {/* </YumAccordionSummary>
      <AccordionDetails> */}
      <div
        style={{
          flexBasis: '100%',
          //flexGrow: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            //marginRight: isRtl ? 0 : 48,
            //marginLeft: !isRtl ? 0 : 48,
          }}
        >
          <div
            style={{
              //marginRight: isRtl ? 'unset' : '10px',
              //marginLeft: !isRtl ? 'unset' : '10px',
              flexGrow: 1,
            }}
          >
            {consistencyGoalInputs}{' '}
          </div>
          {!hideRemove && (
            <IconButton
              style={{
                flexShrink: 0,
                marginTop: '2px',
                position: 'relative',
                right: isRtl ? 'unset' : '-10px',
                left: !isRtl ? 'unset' : '-10px',
              }}
              title={t`Remove goal`}
              aria-label={t`Remove goal`}
              //onClick={() => remove(index)}
              onClick={handleRemoveGoal}
            >
              <RemoveIcon
                style={{
                  width: 24,
                  height: 24,
                }}
              />
            </IconButton>
          )}
        </div>
      </div>
      {/* </AccordionDetails> */}
      {/* </YumAccordion> */}
    </>
  )
}
